/** 두 엔진이 공유하는 관측 유효성·구간 정책. 변환 recipe나 물리 calibration을 판정하지 않는다. */
import type { PoseSeries } from "../domain/viewerTypes";
import { findTimeSpan, type PoseWxyz } from "./poseMath";

export type PoseSample = {
  status: "sample" | "held" | "empty" | "before" | "stale" | "gap" | "invalid";
  pose: PoseWxyz | null;
  row: number | null;
  sampleTime: number | null;
};

/**
 * 표본 상태의 사용자 문구. 상태 union 바로 옆에 두어 상태를 더하면 문구도 강제된다 —
 * 3D 와 채널 표가 같은 시각을 같은 말로 설명해야 한다.
 */
export const SAMPLE_STATUS_LABELS: Record<PoseSample["status"], string> = {
  sample: "실제 표본",
  held: "직전 표본",
  empty: "표본 없음",
  before: "관측 시작 전",
  stale: "관측 종료",
  gap: "관측 공백",
  invalid: "유효하지 않은 표본",
};

export function isValidPose(pose: PoseWxyz | null): pose is PoseWxyz {
  if (pose === null || !pose.every(Number.isFinite)) return false;
  const norm = Math.hypot(...pose.slice(3));
  return Number.isFinite(norm) && norm > 0;
}

/** 기본 gap은 양의 샘플 간격 중앙값의 5배다. 이는 FE 표시 정책이며 품질 판정이 아니다. */
export function preparePoseSeries(
  timestampsSec: readonly number[],
  poses: readonly (PoseWxyz | null)[],
  maxGapSeconds?: number,
): PoseSeries {
  const intervals = timestampsSec.slice(1).map((time, i) => time - timestampsSec[i]);
  const timelineValid =
    timestampsSec.length === poses.length &&
    timestampsSec.every(Number.isFinite) &&
    intervals.every((delta) => delta > 0);
  const sorted = intervals.filter((delta) => delta > 0).sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const median =
    sorted.length === 0
      ? 0
      : sorted.length % 2 === 0
        ? (sorted[middle - 1] + sorted[middle]) / 2
        : sorted[middle];
  const gap = maxGapSeconds ?? median * 5;
  return {
    timestampsSec,
    poses: poses.map((pose) => (isValidPose(pose) ? pose : null)),
    maxGapSeconds: gap,
    timelineValid: timelineValid && Number.isFinite(gap) && gap >= 0,
  };
}

/**
 * `seconds` 의 포즈 — 표본 시각이면 그 표본, 표본 사이면 직전 표본이다. 두 표본 사이를 보간하지
 * 않는다(원본에 없던 포즈를 그리지 않는다).
 */
export function samplePoseAt(series: PoseSeries, seconds: number): PoseSample {
  const { timestampsSec: times, poses } = series;
  const unavailable = (status: PoseSample["status"], row: number | null = null): PoseSample => ({
    status,
    pose: null,
    row,
    sampleTime: row === null ? null : times[row],
  });
  if (!series.timelineValid || !Number.isFinite(seconds)) return unavailable("invalid");
  if (times.length === 0) return unavailable("empty");
  if (seconds < times[0]) return unavailable("before");
  if (seconds > times[times.length - 1]) return unavailable("stale", times.length - 1);
  const { lo, hi } = findTimeSpan(times, seconds);
  const pose = poses[lo];
  if (pose === null) return unavailable("invalid", lo);
  if (seconds === times[lo]) return { status: "sample", pose, row: lo, sampleTime: times[lo] };
  if (times[hi] - times[lo] > series.maxGapSeconds) return unavailable("gap", lo);
  return { status: "held", pose, row: lo, sampleTime: times[lo] };
}

/** 정적 궤적도 null과 gap을 넘어 연결하지 않는다. */
export function poseSegments(series: PoseSeries): PoseWxyz[][] {
  if (!series.timelineValid) return [];
  const segments: PoseWxyz[][] = [];
  let current: PoseWxyz[] = [];
  series.poses.forEach((pose, i) => {
    if (
      pose === null ||
      (i > 0 && series.timestampsSec[i] - series.timestampsSec[i - 1] > series.maxGapSeconds)
    ) {
      if (current.length > 0) segments.push(current);
      current = [];
    }
    if (pose !== null) current.push(pose);
  });
  if (current.length > 0) segments.push(current);
  return segments;
}
